import { RcContext } from "../context/rcContext";
import { DeviceContext } from "../context/deviceContext";
import { IDeviceCanvas, SOTIHTMLCanvasElement } from "..";

export class DeviceScreenRenderingService {

    public static convertColorToAlpha(img: HTMLImageElement): HTMLCanvasElement {
        const tmpCanvas = document.createElement("canvas"); // Create canvas to get imageData
        tmpCanvas.width = img.width;
        tmpCanvas.height = img.height;
        const tmpCanvasContext = tmpCanvas.getContext("2d");
        tmpCanvasContext.fillStyle = "white";
        tmpCanvasContext.fillRect(0, 0, tmpCanvas.width, tmpCanvas.height);
        tmpCanvasContext.drawImage(img, 0, 0);
        const imgData = tmpCanvasContext.getImageData(0, 0, img.width, img.height);
        const color_r = imgData.data[0];
        const color_g = imgData.data[1];
        const color_b = imgData.data[2];

        for (let i = 0, len = imgData.data.length; i < len; i += 4) {
            const r = imgData.data[i];
            const g = imgData.data[i + 1];
            const b = imgData.data[i + 2];
            if (r === color_r && g === color_g && b === color_b) {
                // Check if the rgb matches the color we want to convert
                imgData.data[i + 3] = 0; // Set alpha 0
            }
        }
        tmpCanvasContext.putImageData(imgData, 0, 0); // Set the canvas' new imageData which has alpha
        return tmpCanvas;
    }

    public static getDrawnDeviceRect(canvas: HTMLCanvasElement) {
        const parent = canvas.offsetParent as HTMLElement;
        const context = canvas.getContext("2d", { willReadFrequently: true });
        const data = context.getImageData(0, 0, canvas.width, canvas.height).data;
        const top = this.getNonEmptyY(data, canvas, true);
        const bottom = this.getNonEmptyY(data, canvas, false);
        const left = this.getNonEmptyX(data, canvas, true);
        const right = this.getNonEmptyX(data, canvas, false);
        const width = right - left;
        const height = bottom - top;
        return {
            height,
            width,
            left,
            top,
            bottom,
            right,
            x: left,
            y: top,
        };
    }

    public static getNonEmptyX(data: Uint8ClampedArray, canvas: HTMLCanvasElement, left: boolean) {
        const offset = left ? 1 : -1;

        for (
            let x = left ? 0 : canvas.width - 1;
            left ? x < canvas.width : x > -1;
            x += offset
        ) {
            for (let y = 0; y < canvas.height; y++) {
                if (!this.isEmpty(this.getRGB(data, canvas, x, y))) {
                    return x;
                }
            }
        }

        return null;
    }

    public static getNonEmptyY(data: Uint8ClampedArray, canvas: HTMLCanvasElement, top: boolean) {
        const offset = top ? 1 : -1;

        for (
            let y = top ? 0 : canvas.height - 1;
            top ? y < canvas.height : y > -1;
            y += offset
        ) {
            for (let x = 0; x < canvas.width; x++) {
                if (!this.isEmpty(this.getRGB(data, canvas, x, y))) {
                    return y;
                }
            }
        }

        return null;
    }

    public static isEmpty(rgb: number[]) {
        const [r, g, b, a] = rgb;
        return r === 0 && g === 0 && b === 0 && a === 0;
    }

    public static getRGB(data: Uint8ClampedArray, canvas: HTMLCanvasElement, x: number, y: number) {
        const offset = (y * canvas.width + x) * 4;
        return [data[offset], data[offset + 1], data[offset + 2], data[offset + 3]];
    }

    public static adjustJpegImage(img, canvasBuffer) {
        canvasBuffer.width = img.width;
        canvasBuffer.height = img.height;
        var canvasContext = canvasBuffer.getContext("2d");

        // flip image vertically
        //canvasContext.save();
        canvasContext.scale(1, -1);
        canvasContext.drawImage(img, 0, -img.height);
        //canvasContext.restore();

        // convert BGRA to RGBA - no needed now by fixing jpeg encoder's color order to BGR
        //var imgData = canvasContext.getImageData(0, 0, img.width, img.height);
        //for (var i = 0, len = imgData.data.length; i < len; i += 4) {
        //    var b = imgData.data[i];
        //    imgData.data[i] = imgData.data[i + 2];
        //    imgData.data[i + 2] = b;
        //}
        //canvasContext.putImageData(imgData, 0, 0);
        return canvasBuffer;
    }

    public static resizeCanvasToTransformed(
        curCanvas: HTMLCanvasElement | IDeviceCanvas,
        canvasToDraw: HTMLCanvasElement,
        rotation: number,
        scale: number,
        swapDimensions: boolean = false
    ): void {
        const rotationInRad = -Math.PI * rotation / 2;
        let coords: number[];
        if (swapDimensions) {
            coords = [
                canvasToDraw.height, 0,
                0, canvasToDraw.width,
                canvasToDraw.height, canvasToDraw.width
            ];
        } else {
            coords = [
                canvasToDraw.width, 0,
                0, canvasToDraw.height,
                canvasToDraw.width, canvasToDraw.height
            ];
        }
        let cx = 0, cy = 0, w = 0, h = 0, tx: number, ty: number;
        for (let i = 0; i < 3; i++) {
            tx = Math.cos(rotationInRad) * coords[0 + (i * 2)] - Math.sin(rotationInRad) * coords[1 + (i * 2)];
            ty = Math.sin(rotationInRad) * coords[0 + (i * 2)] + Math.cos(rotationInRad) * coords[1 + (i * 2)];
            if (tx < cx) cx = tx;
            if (ty < cy) cy = ty;
            if (tx > w) w = tx;
            if (ty > h) h = ty;
        }
        // Convert result to integer
        if (cx > 0) cx = Math.floor(cx); else cx = Math.ceil(cx);
        if (cy > 0) cy = Math.floor(cy); else cy = Math.ceil(cy);

        const canvasElement: SOTIHTMLCanvasElement = !!(<IDeviceCanvas>curCanvas).element ? (<IDeviceCanvas>curCanvas).element : <SOTIHTMLCanvasElement>curCanvas;
        canvasElement.cx = cx;
        canvasElement.cy = cy;

        if (w > 0) w = Math.floor(w); else w = Math.ceil(w);
        if (h > 0) h = Math.floor(h); else h = Math.ceil(h);
        // Adjust to our new width and height
        const newWidth = (-cx + w) * scale;
        const newHeight = (-cy + h) * scale;
        if (curCanvas.width !== newWidth) {
            curCanvas.width = newWidth;
        }
        if (curCanvas.height !== newHeight) {
            curCanvas.height = newHeight;
        }
    }

    public static drawTransformedToCanvas(
        curCanvas: HTMLCanvasElement,
        canvasToDraw: HTMLCanvasElement,
        screenOffsetX: number,
        screenOffsetY: number,
        rotation: number,
        scale: number,
        targetScale: number = 0,
        skin: boolean = undefined,
        reverse: boolean = undefined
    ): void {
        if (canvasToDraw.width === 0 || canvasToDraw.height === 0) {
            return;
        }

        const curCanvasContext = curCanvas.getContext("2d");
        const rotationInRad = -Math.PI * rotation / 2;
        // Get our stored translation offset to keep the images inside the canvas during rotation
        let cx = (<any>curCanvas)?.cx || 0, cy = (<any>curCanvas)?.cy || 0;

        let ctx = <DeviceContext>(RcContext.instance.deviceContext)

        curCanvasContext.save();

        // Apply our transformation to keep the canvasToDraw drawn inside the curCanvas
        if (skin !== undefined && skin) {
            curCanvasContext.translate(-cx * scale / targetScale, -cy * scale / targetScale);
        } else {
            curCanvasContext.translate(-cx * scale, -cy * scale);
        }
        if ((<any>curCanvas).needTranslate) {
            curCanvasContext.translate(ctx.left, ctx.top);
        }
        curCanvasContext.rotate(rotationInRad);

        let width = canvasToDraw.width * scale;
        let height = canvasToDraw.height * scale;
        if (targetScale !== undefined && targetScale !== 0) {
            width /= targetScale;
            height /= targetScale;
        }

        let y = screenOffsetY * scale;
        if (reverse !== undefined && reverse) {
            curCanvasContext.scale(1, -1);
            y -= height;
        }

        if (!(<any>canvasToDraw).emptySkin) {
            curCanvasContext.drawImage(canvasToDraw, screenOffsetX * scale, y, width, height);
        }

        curCanvasContext.restore();
    }

    static drawTransformedToFitSkin(curCanvas: HTMLCanvasElement, canvasToDraw: HTMLCanvasElement, screenOffsetX: number, screenOffsetY: number, rotation: number, scale: number, skinWidth: number, skinHeight: number): void {
        if (canvasToDraw.width === 0 || canvasToDraw.height === 0) {
            return;
        }
        const curCanvasContext = curCanvas.getContext("2d");
        const rotationInRad = -Math.PI * rotation / 2;
        // Get our stored translation offset to keep the images inside the canvas during rotation
        let cx = (<any>curCanvas)?.cx || 0, cy = (<any>curCanvas)?.cy || 0;

        let ctx = <DeviceContext>(RcContext.instance.deviceContext)

        curCanvasContext.save();
        // Apply our transformation to keep the canvasToDraw drawn inside the curCanvas
        curCanvasContext.translate(-cx * scale, -cy * scale);

        if ((<any>curCanvas).needTranslate) {
            curCanvasContext.translate(ctx.left, ctx.top);
        }

        curCanvasContext.rotate(rotationInRad);
        curCanvasContext.drawImage(canvasToDraw, screenOffsetX * scale, screenOffsetY * scale, skinWidth * scale, skinHeight * scale);
        curCanvasContext.restore();
    }

    public static createImageFromBinaryData(
        binData: ArrayBuffer,
        mimeType: string,
        loadCallback?: (image: HTMLImageElement) => void,
        errorCallback?: (image: HTMLImageElement) => void
    ): HTMLImageElement {
        const image: HTMLImageElement = new Image();

        // we need a temporary property for the old image objURL, until the image is loaded
        // This was likely done to avoid flickering images, keep the behavior until
        // fully tested.
        image.onload = function () {
            if (loadCallback && typeof loadCallback === 'function') {
                loadCallback(image);
            }
        };

        image.onerror = function (e) {
            // console.log(e);
            if (errorCallback && typeof errorCallback === 'function') {
                errorCallback(image);
            }
        };

        /**
         * TODO 2024.0.4 RC Refactor - There will be a long stall time for Image.onload event
         * when using createObjURL. Using base64 as a replacement is faster than URL.
         * Still need further investigation.
         */
        const base64String = this.arrayBufferToBase64(binData);
        const dataURI = `data:${mimeType};base64,${base64String}`;
        image.src = dataURI;

        return image;
    }

    public static arrayBufferToBase64(buffer: ArrayBuffer): string {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }

    public static drawCurrentTransformationOnSkinCanvas(context: any): void {
        if (!context.imageSkin) return;

        // Clear existing skin canvas or create a new one
        if (context.skinCanvas) {
            const ctx = context.skinCanvas.getContext("2d");
            ctx.clearRect(0, 0, context.skinCanvas.width, context.skinCanvas.height); // Clear current canvas
        } else {
            context.skinCanvas = document.createElement("canvas");
        }

        // Draw skin transformed on canvas
        this.resizeCanvasToTransformed(context.skinCanvas, context.imageSkin, context.rotation + context.clientSideRotation, context.scale);
        this.drawTransformedToCanvas(context.skinCanvas, context.imageSkin, 0, 0, context.rotation + context.clientSideRotation, context.scale);
    }
}
